import fetch from 'cross-fetch';

import { getApiConfig } from '@utils/getApiConfig';

/**
 * TA-11025: the email OTP endpoints, shared by every registration flow that
 * has to prove email ownership (web RegisterForm on v4 already did this
 * inline in RegisterVerifyEmail.web; the v3 invite forms and native email
 * sign-up now need the same two calls).
 *
 * `check` does not consume the code on the backend — the registration call
 * that follows sends the same code as `user.email_verification_code` and is
 * what actually spends it.
 */

export const EMAIL_VERIFICATION_CODE_LENGTH = 6;

export type EmailVerificationResult =
  | { ok: true }
  | { ok: false; error: string | null };

const postJson = async (url: string, body?: unknown) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });
  return response.json().catch(() => null);
};

const readError = (json: unknown): string | null => {
  const error = (json as { error?: unknown } | null)?.error;
  return typeof error === 'string' && error ? error : null;
};

const isSuccess = (json: unknown) => {
  const success = (json as { success?: unknown } | null)?.success;
  return success === true || success === 'true';
};

export const sendEmailVerificationCode = async (
  email: string
): Promise<EmailVerificationResult> => {
  const url = `${
    getApiConfig().apiUrl
  }/api/v4/send_email_verification_code.json?email=${encodeURIComponent(
    email.toLowerCase()
  )}`;
  const json = await postJson(url);
  const error = readError(json);
  // Same reading as RegisterVerifyEmail.web: an explicit `success: false`
  // without a message is still a failure; anything else without an error is
  // a sent code.
  if (error) return { ok: false, error };
  if ((json as { success?: unknown } | null)?.success === false) {
    return { ok: false, error: null };
  }
  return { ok: true };
};

export const checkEmailVerificationCode = async (
  email: string,
  code: string
): Promise<EmailVerificationResult> => {
  const json = await postJson(
    `${getApiConfig().apiUrl}/api/v4/check_email_verification_code.json`,
    { email: email.toLowerCase(), code }
  );
  if (isSuccess(json)) return { ok: true };
  return { ok: false, error: readError(json) };
};
